import {
  CHAT_PROVIDERS,
  FLOW_TYPES,
  RECORDING_PROVIDERS,
  RTC_PROVIDERS,
  SESSION_MODES,
  SIGNALING_PROVIDERS,
  TRANSCRIPTION_PROVIDERS,
} from '../providerIds'
import { normalizeResolvedVideoCallQuality } from './videoCallQualityContract'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeEnumValue(value, allowedValues, fallbackValue) {
  return allowedValues.includes(value) ? value : fallbackValue
}

export const SESSION_CAPABILITY_DEFAULTS = Object.freeze({
  screenshare: false,
  remoteMute: false,
  auditObserverJoin: false,
  chamberScreenControl: false,
  recording: false,
  liveTranscription: false,
  captions: false,
})

export function createSessionCapabilities(overrides = {}) {
  return {
    ...SESSION_CAPABILITY_DEFAULTS,
    ...(isPlainObject(overrides) ? overrides : {}),
  }
}

export function normalizeSessionMode(sessionMode, fallbackValue = SESSION_MODES.online) {
  return normalizeEnumValue(sessionMode, Object.values(SESSION_MODES), fallbackValue)
}

export function normalizeFlowType(flowType, fallbackValue = FLOW_TYPES.consumerLiveSession) {
  return normalizeEnumValue(flowType, Object.values(FLOW_TYPES), fallbackValue)
}

export function normalizeRtcProvider(rtcProvider, fallbackValue = RTC_PROVIDERS.agora) {
  return normalizeEnumValue(rtcProvider, Object.values(RTC_PROVIDERS), fallbackValue)
}

export function normalizeSignalingProvider(
  signalingProvider,
  fallbackValue = SIGNALING_PROVIDERS.agoraRtm,
) {
  return normalizeEnumValue(
    signalingProvider,
    Object.values(SIGNALING_PROVIDERS),
    fallbackValue,
  )
}

export function normalizeChatProvider(chatProvider, fallbackValue = CHAT_PROVIDERS.agoraChat) {
  if (chatProvider === null) {
    return null
  }

  return normalizeEnumValue(chatProvider, Object.values(CHAT_PROVIDERS), fallbackValue)
}

export function createSessionRoutingDecision(overrides = {}) {
  const flowType = normalizeFlowType(overrides?.flowType)
  const sessionMode = normalizeSessionMode(overrides?.sessionMode)
  const rtcProvider = normalizeRtcProvider(overrides?.rtcProvider)

  return {
    sessionMode,
    flowType,
    rtcProvider,
    signalingProvider: normalizeSignalingProvider(overrides?.signalingProvider),
    chatProvider: normalizeChatProvider(overrides?.chatProvider),
    recordingProvider: normalizeRecordingProvider(overrides?.recordingProvider),
    transcriptionProvider: normalizeTranscriptionProvider(overrides?.transcriptionProvider),
    resolvedByPolicy:
      typeof overrides?.resolvedByPolicy === 'string' && overrides.resolvedByPolicy.trim()
        ? overrides.resolvedByPolicy
        : 'legacy-default',
    fallbackProviders: normalizeFallbackProviders(overrides?.fallbackProviders, rtcProvider),
    capabilities: createSessionCapabilities(overrides?.capabilities),
    providerPayloads: isPlainObject(overrides?.providerPayloads) ? overrides.providerPayloads : {},
    featureFlags: isPlainObject(overrides?.featureFlags) ? overrides.featureFlags : {},
    videoQuality: normalizeResolvedVideoCallQuality(overrides?.videoQuality),
  }
}

export function isOnlineSessionMode(sessionMode) {
  return sessionMode === SESSION_MODES.online
}

export function requiresRecording(sessionRoutingDecision) {
  return sessionRoutingDecision?.capabilities?.recording === true
}

export function normalizeRecordingProvider(recordingProvider) {
  if (!recordingProvider) {
    return null
  }

  if (Object.values(RECORDING_PROVIDERS).includes(recordingProvider)) {
    return recordingProvider
  }

  return null
}

export function normalizeTranscriptionProvider(
  transcriptionProvider,
  fallbackValue = TRANSCRIPTION_PROVIDERS.none,
) {
  return normalizeEnumValue(
    transcriptionProvider,
    Object.values(TRANSCRIPTION_PROVIDERS),
    fallbackValue,
  )
}

export function normalizeFallbackProviders(
  fallbackProviders,
  fallbackProvider = RTC_PROVIDERS.agora,
) {
  const normalizedFallbackProvider = normalizeRtcProvider(fallbackProvider)

  if (!Array.isArray(fallbackProviders)) {
    return [normalizedFallbackProvider]
  }

  const normalizedProviders = fallbackProviders.filter((provider, index, allProviders) => {
    return (
      Object.values(RTC_PROVIDERS).includes(provider) && allProviders.indexOf(provider) === index
    )
  })

  return normalizedProviders.length > 0 ? normalizedProviders : [normalizedFallbackProvider]
}
